(ns ballot-scout-v2-frontend.voter-lookup.views
  (:require
   [ballot-scout-v2-frontend.components.click-outside :refer [click-outside]]
   [ballot-scout-v2-frontend.components.export :refer [export-menu]]
   [ballot-scout-v2-frontend.components.icon :refer [icon] :as icon]
   [ballot-scout-v2-frontend.components.pagination :refer [pagination]]
   [ballot-scout-v2-frontend.components.search :refer [clear-button]]
   [ballot-scout-v2-frontend.events :as events]
   [ballot-scout-v2-frontend.request-helpers :as request-helpers]
   [ballot-scout-v2-frontend.subs :as subs]
   [ballot-scout-v2-frontend.util :as util :refer [remove-empty-values]]
   [ballot-scout-v2-frontend.voter-lookup.common :refer [default-search-state]]
   [ballot-scout-v2-frontend.voter-lookup.events :as e.voter-lookup]
   [ballot-scout-v2-frontend.voter-lookup.subs :as s.voter-lookup]
   [ballot-scout-v2-frontend.voter-lookup.views.filters :as v.filters]
   [ballot-scout-v2-frontend.voter-lookup.views.search :as v.search]
   [clojure.string :as str]
   [re-frame.core :as rf]
   [reagent.core :as r]))

; statuses for which we know the timestamps won't be accurate, so we omit them
(def no-timestamp-statuses #{"disposition/mailed" "disposition/received" "disposition/accepted" "disposition/flagged"})

(defn delivered-status-from-ballot
  "Determines whether a status ought to be displayed as delivered, based on
   whether a mailing that is out-for-delivery has an estimated delivery date
   in the past"
  [full-ballot]
  (-> (cond (and (= "voter-bound/out-for-delivery" (:ballot/status full-ballot))
                 (util/after-day? (:mailpiece/estimated-delivery-date (:ballot/voter-bound-mailpiece full-ballot))))
            :voter-bound/delivered
            (and (= "office-bound/out-for-delivery" (:ballot/status full-ballot))
                 (util/after-day? (:mailpiece/estimated-delivery-date (:ballot/office-bound-mailpiece full-ballot))))
            :office-bound/delivered
            ;else - just return the original status
            (not (nil? (:ballot/status full-ballot)))
            (:ballot/status full-ballot))
      keyword
      util/backend-status->label))

(defn delivered-date-from-mailpiece
  "If a mailpiece is marked delivered, then delievered date is last scan time.
   Otherwise, if we have an estimated delivery assoc that."
  [mailpiece]
  (cond (= "delivered" (:mailpiece/current-status mailpiece))
        (:mailpiece/lastscan-ts mailpiece)
               ; otherwise, if we have an estimated delivery, then show it
        (not (nil? (:mailpiece/estimated-delivery-date mailpiece)))
        (util/date-str-to-date (:mailpiece/estimated-delivery-date mailpiece))))

(defn- voter-address [{:ballot/keys [voter]} & line-break?]
  (let [line1 (:voter/address-1 voter)
        line2 (:voter/address-2 voter)
        line3 (str (:voter/city voter) ", " (:voter/state voter) " " (:voter/zip voter))]
    (if line-break?
      [:div [:div line1
             (when line2
               [:div line2])]
       [:div line3]]
      [:div.is-flex (str line1 " " line2 " " line3)])))

(defn- last-scan-received [ballot]
  [:div [:div (if (contains? no-timestamp-statuses (:ballot/status ballot))
                (util/render-weekday-month-date (:ballot/lastevent-ts ballot))
                (util/render-datetime-ampm (:ballot/lastevent-ts ballot)))]
   (when (not-empty (:ballot/lastevent-city ballot))
     [:div (str (:ballot/lastevent-city ballot) ", "
                (:ballot/lastevent-state ballot) " "
                (:ballot/lastevent-zip ballot))])])

(defn display-history
  [e ballot-info]
  (.preventDefault e)
  (rf/dispatch [::e.voter-lookup/fetch-ballot-history ballot-info]))

(defn show-history-button [ballot]
  [:button {:on-click (fn [e] (display-history e ballot))
            :class "is-clickable"}
   "View History"])

(defn- table-row [ballot columns]
  [:tr
   ^{:key (:ballot/id ballot)} ; row-specific unique id
   (for [c columns]
     (let [render-cell (:render c)]
       ^{:key (:name c)} [:td (render-cell ballot)]))])

(defn sortable-th [{:keys [sort name]} asc? desc? lookup-route get-lookup-route-params search-state]
  [:th {:class "sortable"
        :on-click (let [transition-to (if asc? :desc :asc)]
                    #(rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                                   lookup-route
                                   (get-lookup-route-params)
                                   (-> @search-state
                                       (assoc :sort-by (transition-to sort))
                                       (dissoc :page)
                                       remove-empty-values)]))}
   [:span name]
   [:span {:class "icon"}
    (cond asc?  [icon ::icon/sort-up]
          desc? [icon ::icon/sort-down]
          :else [icon ::icon/sort])]])

(defn ballot-event->label [{:keys [resolved-status source] :as _ballot-event}]
  (let [readable-status (util/backend-status->label (keyword resolved-status))]
    [:div
     readable-status
     (when (and (contains? #{:voter-bound/delivered :office-bound/delivered} (keyword resolved-status))
                (= source "system"))
       [:div.is-size-7 " (assumed based on anticipated delivery date)"])]))

(defn history-row
  [e]
  (let [{:keys [resolved-status ts scan-city scan-state scan-zip estimated-delivery-date]} e]
    [:tr
     ; last scan datetime
     [:td (if (contains? no-timestamp-statuses resolved-status)
            (util/render-weekday-month-date ts)
            (util/render-datetime-ampm ts))]
     ; last scan location
     [:td (str scan-city " " scan-state " " scan-zip)]
     ; user-facing status
     [:td (ballot-event->label e)]
     [:td (when (clojure.string/includes? (str resolved-status) "voter-bound")
            (when estimated-delivery-date
              [:div [:span (util/render-weekday-month-date
                            (util/date-str-to-date estimated-delivery-date))]
               (when (= true (:updated-edd e)) [:div.is-size-7 " (updated)"])]))]
     [:td (when (clojure.string/includes? (str resolved-status) "office-bound")
            (when estimated-delivery-date
              [:div [:span (util/render-weekday-month-date
                            (util/date-str-to-date estimated-delivery-date))]
               (when (= true (:updated-edd e)) [:div.is-size-7 " (updated)"])]))]]))

(defn history-table
  [event-data]
  [:table.table.is-striped.is-bordered.history-table.paragraph-2
   [:thead
    [:tr.modal-header
     [:th.sticky "Date and Time"]
     [:th.sticky "Location"]
     [:th.sticky "Ballot Status"]
     [:th.sticky "Anticipated Voter Delivery Date"]
     [:th.sticky "Anticipated Office Delivery Date"]]]
   [:tbody
    (for [row-data event-data]
      ^{:key (:event (:event/scan-ts row-data))}
      [history-row row-data])]])

(defn hide-ballot-history-handler [e]
  (.preventDefault e)
  (rf/dispatch [::e.voter-lookup/clear-ballot-history]))

(defn history-modal
  []
  (let [ballot-details @(rf/subscribe [::s.voter-lookup/show-ballot-history])
        ballot-history @(rf/subscribe [::s.voter-lookup/ballot-history-response])
        vb-mailpiece (:ballot/voter-bound-mailpiece ballot-history)
        ob-mailpiece (:ballot/office-bound-mailpiece ballot-history)
        event-data  @(rf/subscribe [::s.voter-lookup/event-data])]
    [:div.modal.is-active
     [:div.modal-background]
     [click-outside
      {:on-click-outside hide-ballot-history-handler}
      (fn [ref]
        [:div.modal-card
         {:ref ref}
         [:div.modal-card-head
          [:p.modal-card-title "Ballot History"]
          [:button {:class "is-clickable"
                    :on-click hide-ballot-history-handler}
           [icon ::icon/times]]]
         [:div.modal-titles
          [:div [:text.bold "Voter Name:"] [:text.info (get-in ballot-details [:ballot/voter :voter/fullname])]]
          [:div.is-flex [:text.bold "Voter Address:"] [:text.info [voter-address ballot-details]]]
          [:div [:text.bold "Date Record Created:"] [:text.info (util/render-date (:ballot/created-ts ballot-details))]]
          [:div.modal-head-spacer]
          [:div [:text.bold "Anticipated Voter Delivery Date:"] [:text.info (util/render-date (delivered-date-from-mailpiece vb-mailpiece))]]
          [:div [:text.bold "Anticipated Office Delivery Date:"] [:text.info (util/render-date (delivered-date-from-mailpiece ob-mailpiece))]]]
         [:section.modal-card-body
          (cond
            (= :loading ballot-history)
            [:div#lookuploader.loading
             [:img {:src "/images/compass-100x100.png"}]]
            (> (count event-data) 0)
            [history-table event-data]
            :else
            [:div "No events have occured yet for this ballot."])]
         [:footer.modal-card-foot]])]]))

(defn- table [{:keys [lookup-route get-lookup-route-params ballots search-state columns]}]
  (let [sort-by (:sort-by (merge default-search-state @search-state))]
    [:table {:class "table is-striped is-hoverable is-bordered paragraph-2"}
     [:thead
      [:tr
       (for [c columns]
         (let [column-sort (:sort c)
               asc? (= sort-by (:asc column-sort))
               desc? (= sort-by (:desc column-sort))]
           ^{:key (:name c)}
           (if column-sort
             [sortable-th c asc? desc? lookup-route get-lookup-route-params search-state]
             [:th (:name c)])))]]
     [:tbody
      (for [b ballots]
        ^{:key (str "tr-" (:ballot/id b))} [table-row b columns])]]))

(defn back-and-export-buttons [{:keys [prep-export! results]}]
  (when (not-empty results)
    (let [export-status (:status @(rf/subscribe [::s.voter-lookup/export]))
          creating-export? (contains? #{:creating :generating} export-status)]
      [:div {:class "column export-button is-flex is-justify-content-flex-end is-align-items-flex-end"}
       [export-menu {:on-click (partial prep-export! @(rf/subscribe [::subs/query-params]))
                     :disabled creating-export?
                     :align-right? true
                     :button-text [:span
                                   (if creating-export?
                                     [icon ::icon/spinner {:pulse? true}]
                                     [icon ::icon/download])
                                   [:span {:class "ml-3 is-hidden-mobile"} "Export Results"]]}]])))

(defn shortcuts [prep-export-fn]
  (let [export-status (:status @(rf/subscribe [::s.voter-lookup/export]))
        creating-export? (contains? #{:creating :generating} export-status)]
    [:div {:class "column is-flex is-justify-content-flex-end is-align-items-flex-end"}
     [export-menu {:on-click (partial prep-export-fn nil)
                   :disabled creating-export?
                   :align-right? true
                   :button-text [:span
                                 (if creating-export?
                                   [icon ::icon/spinner {:pulse? true}]
                                   [icon ::icon/download])
                                 [:span {:class "ml-3 is-hidden-mobile"} "Export All Voters"]]}]]))

(defn export-view []
  (r/with-let [export-state (rf/subscribe [::s.voter-lookup/export])]
    [:div {:class (str "voter-lookup-export"
                       (when (= (:status @export-state) :ready) " expanded"))}
     (case (:status @export-state)
       :ready [:div {:class "alert-success button-in p-4"}
               [:strong "Your export is ready! "]
               [:a {:class "has-text-underlined"
                    :href (:link @export-state)
                    :download "ballot-export.csv"}
                "Download " (util/get-readable-filename-from-s3-link (:link @export-state))]
               [clear-button #(rf/dispatch [::e.voter-lookup/clear-export-state])]]

       :empty-export [:div {:class "title is-4"}
                      "No ballots found for export"]

       :errored [:div {:class "title is-4"}
                 "There was an error with your export"]
       nil)]
    (finally
      (rf/dispatch [::e.voter-lookup/clear-export-state]))))

(defn search-and-export
  [lookup-route get-lookup-route-params prep-export-fn]
  (r/with-let [response (rf/subscribe [::s.voter-lookup/response])
               url-state (rf/subscribe [::subs/query-params])]
    (let [address-1-query-param (:voter.address-1 @url-state)
          city-state-zip-query-param (:voter.city-state-zip @url-state)
          active-tab (r/atom (if (or address-1-query-param city-state-zip-query-param)
                               :address
                               :name-or-id))
          filters-value (r/atom (:or @url-state)) ;; Right now, we're just working on the OR'd statuses
          show-filters-dropdown? (r/atom (some? @filters-value))
          name-search-value (r/atom (or nil (:query @url-state)))
          address-1-search-value (r/atom (or nil address-1-query-param))
          city-state-zip-search-value (r/atom (or nil city-state-zip-query-param))]
      (fn []
        [:div
         [:div {:class "pt-5 mb-1 columns"}
          [:div {:class ["column" "is-three-fifths"]}
           [:div {:class "tabs is-small mb-0 px-1"}
            [:ul
             [:li {:class (when (= @active-tab :name-or-id) "is-active has-text-weight-bold")}
              [:a {:class "p-0"
                   :on-click #(reset! active-tab :name-or-id)}
               "Search by Voter Name or ID"]]
             [:div {:class "vl mx-2 is-hidden-mobile"}]
             [:li {:class (when (= @active-tab :address) "is-active has-text-weight-bold")}
              [:a {:class "p-0"
                   :on-click #(reset! active-tab :address)}
               "Search by Voter Address"]]]]
           (cond
             (= @active-tab :name-or-id)
             [v.search/name-search-form
              lookup-route get-lookup-route-params url-state
              show-filters-dropdown?
              name-search-value
              address-1-search-value city-state-zip-search-value]

             (= @active-tab :address)
             [v.search/address-search-form
              lookup-route get-lookup-route-params url-state
              show-filters-dropdown?
              name-search-value
              address-1-search-value city-state-zip-search-value])]
          (if (some @url-state #{:query :status :or
                                 :import-id :disposition-id
                                 :last-seen-city :last-seen-state
                                 :voter.address-1 :voter.city-state-zip
                                 :created :created-gte :created-lte
                                 :last-event :last-event-gte :last-event-lte
                                 :vb-edd :vb-edd-gte :vb-edd-lte
                                 :ob-edd :ob-edd-gte :ob-edd-lte})
            [back-and-export-buttons {:prep-export! prep-export-fn
                                      :results (:results @response)}]
            [shortcuts prep-export-fn])]

         (when @show-filters-dropdown?
           [v.filters/filter-drawer lookup-route get-lookup-route-params])

         [v.filters/applied-filters lookup-route get-lookup-route-params]

         [export-view]]))))

(defn paginate [route route-params url-state pageno]
  (rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                route
                route-params
                (-> url-state (assoc :page pageno) remove-empty-values)]))

(defn voter-lookup [{:keys [prep-export-fn
                            lookup-route
                            get-lookup-route-params
                            columns]}]
  (r/with-let [response (rf/subscribe [::s.voter-lookup/response])
               url-state (rf/subscribe [::subs/query-params])]
    [:div {:class "voter-lookup is-flex is-flex-direction-column"}
     [:h1 {:class "heading-1 mb-0"} "Voter Lookup"]

     (when (some? @(rf/subscribe [::s.voter-lookup/show-ballot-history]))
       [history-modal])

     [search-and-export lookup-route get-lookup-route-params prep-export-fn]

     (let [{:keys [disposition-id import-id]} @url-state
           import @(rf/subscribe [::subs/import-by-id import-id])
           waiting-for-import? (if import-id (not import) false)
           disposition @(rf/subscribe [::subs/disposition-by-id disposition-id])
           waiting-for-disposition? (if disposition-id (not disposition) false)
           ready? (and (:results @response) (not waiting-for-import?) (not waiting-for-disposition?))]
       [:<>
        [:div {:class "table-container pb-6"}
         (if ready?
           (if (empty? (:results @response))
             [:div {:class "title is-5"} "No Ballots Found"]

             (let [response-length (:total-results @response)]
               [:<>
                [:div {:class "mb-4 results-text"} response-length (str " Result" (when (> response-length 1) "s"))]
                [table {:ballots (:results @response)
                        :columns columns
                        :lookup-route lookup-route
                        :get-lookup-route-params get-lookup-route-params
                        :search-state url-state}]]))
           [:div#lookuploader.loading {:class "title is-5 pt-6"}
            [:img {:src "/images/compass-100x100.png"}]])]

        (when ready?
          [pagination {:curr-page (:page (merge default-search-state @url-state))
                       :total-pages (:total-pages @response)
                       :on-page (partial paginate
                                         lookup-route
                                         (get-lookup-route-params)
                                         @url-state)}])])]))

;; Jurisdiction view components

(defn jurisdiction-prep-export!
  [query-state mimetype]
  (rf/dispatch
   [::e.voter-lookup/create-export-for-jurisdiction
    (:jurisdiction/id @(rf/subscribe [::subs/current-jurisdiction]))
    (:election/id @(rf/subscribe [::subs/current-election]))
    query-state
    mimetype]))

(def jurisdiction-lookup-route :ballot-scout-v2-frontend.routes/jurisdiction-voter-lookup)
(defn jurisdiction-lookup-route-params [] {:partner-slug @(rf/subscribe [::subs/current-partner-slug])
                                           :jurisdiction-slug @(rf/subscribe [::subs/current-jurisdiction-slug])
                                           :election-slug @(rf/subscribe [::subs/current-election-slug])})

(def jurisdiction-columns
  [{:name "Date Created"
    :render #(-> % :ballot/created-ts util/render-weekday-month-date)
    :sort {:asc "created-ts" :desc "-created-ts"}}
   {:name "Voter ID"
    :render #(get-in % [:ballot/voter :voter/external-id])
    :sort {:asc "voter.id" :desc "-voter.id"}}
   {:name "Voter Name"
    :render #(get-in % [:ballot/voter :voter/fullname])
    :sort {:asc "voter.fullname" :desc "-voter.fullname"}}
   {:name "Voter Address"
    :render #(voter-address % true)
    :sort {:asc "voter.address" :desc "-voter.address"}}
   {:name "Ballot Status"
    :render #(delivered-status-from-ballot %)
    :sort {:asc "status" :desc "-status"}}
   {:name "Last Scan Received"
    :render last-scan-received
    :sort {:asc "lastevent-ts" :desc "-lastevent-ts"}}
   {:name "Anticipated Voter Delivery Date"
    :render #(-> % :ballot/voter-bound-mailpiece
                 delivered-date-from-mailpiece
                 util/render-weekday-month-date)
    :sort {:asc "vb-mailpiece.estimated-delivery-date" :desc "-vb-mailpiece.estimated-delivery-date"}}
   {:name "Anticipated Office Delivery Date"
    :render #(-> % :ballot/office-bound-mailpiece
                 delivered-date-from-mailpiece
                 util/render-weekday-month-date)
    :sort {:asc "ob-mailpiece.estimated-delivery-date" :desc "-ob-mailpiece.estimated-delivery-date"}}
   {:name "Ballot History"
    :render show-history-button}])

(defn jurisdiction-page []
  (let [current-jurisdiction-slug @(rf/subscribe [::subs/current-jurisdiction-slug])
        current-jurisdiction @(rf/subscribe [::subs/current-jurisdiction])
        current-election @(rf/subscribe [::subs/current-election])
        url-state (rf/subscribe [::subs/query-params])
        fetch-error (or @(rf/subscribe [::request-helpers/failed-request
                                        [::events/fetch-jurisdiction
                                         current-jurisdiction-slug]])
                        @(rf/subscribe [::request-helpers/failed-request
                                        [::events/fetch-elections-for-jurisdiction
                                         current-jurisdiction-slug]])
                        @(rf/subscribe [::request-helpers/failed-request
                                        [::e.voter-lookup/jurisdiction-fetch-ballots
                                         {:jurisdiction-id (:jurisdiction/id current-jurisdiction)
                                          :election-id (:election/id current-election)
                                          :query-params @url-state}]]))]
    (cond
      fetch-error
      (:status-text fetch-error)

      (and current-jurisdiction current-election)
      [voter-lookup {:columns jurisdiction-columns
                     :get-lookup-route-params jurisdiction-lookup-route-params
                     :lookup-route jurisdiction-lookup-route
                     :prep-export-fn jurisdiction-prep-export!}]

      (some? @(rf/subscribe [::subs/elections-for-jurisdiction current-jurisdiction-slug]))
      "Election Does Not Exist"

      :else
      "Loading Partner Data")))

;; Partner view components

(defn partner-prep-export!
  [query-state mimetype]
  (rf/dispatch
   [::e.voter-lookup/create-export-for-partner
    (:partner/id @(rf/subscribe [::subs/current-partner]))
    (:election/id @(rf/subscribe [::subs/current-election]))
    query-state
    mimetype]))

(def partner-lookup-route :ballot-scout-v2-frontend.routes/partner-voter-lookup)
(defn partner-lookup-route-params [] {:partner-slug @(rf/subscribe [::subs/current-partner-slug])
                                      :election-slug @(rf/subscribe [::subs/current-election-slug])})

(def partner-columns
  [{:name "Date Created"
    :render #(-> % :ballot/created-ts util/render-weekday-month-date)
    :sort {:asc "created-ts" :desc "-created-ts"}}
   {:name "Voter Jurisdiction"
    :render #(get-in % [:ballot/jurisdiction :jurisdiction/name])
    :sort {:asc "jurisdiction.name" :desc "-jurisdiction.name"}}
   {:name "Voter ID"
    :render #(get-in % [:ballot/voter :voter/external-id])
    :sort {:asc "voter.id" :desc "-voter.id"}}
   {:name "Voter Name"
    :render #(get-in % [:ballot/voter :voter/fullname])
    :sort {:asc "voter.fullname" :desc "-voter.fullname"}}
   {:name "Voter Address"
    :render #(voter-address % true)
    :sort {:asc "voter.address" :desc "-voter.address"}}
   {:name "Ballot Status"
    :render #(delivered-status-from-ballot %)
    :sort {:asc "status" :desc "-status"}}
   {:name "Last Scan Received"
    :render last-scan-received
    :sort {:asc "lastevent-ts" :desc "-lastevent-ts"}}
   {:name "Anticipated Voter Delivery Date"
    :render #(-> % :ballot/voter-bound-mailpiece
                 delivered-date-from-mailpiece
                 util/render-weekday-month-date)
    :sort {:asc "vb-mailpiece.estimated-delivery-date" :desc "-vb-mailpiece.estimated-delivery-date"}}
   {:name "Anticipated Office Delivery Date"
    :render #(-> % :ballot/office-bound-mailpiece
                 delivered-date-from-mailpiece
                 util/render-weekday-month-date)
    :sort {:asc "ob-mailpiece.estimated-delivery-date" :desc "-ob-mailpiece.estimated-delivery-date"}}
   {:name "Ballot History"
    :render show-history-button}])

(defn partner-page []
  (let [current-partner-slug @(rf/subscribe [::subs/current-partner-slug])
        current-partner @(rf/subscribe [::subs/current-partner])
        current-election @(rf/subscribe [::subs/current-election])
        url-state (rf/subscribe [::subs/query-params])
        fetch-error (or @(rf/subscribe [::request-helpers/failed-request
                                        [::events/fetch-jurisdiction
                                         current-partner-slug]])
                        @(rf/subscribe [::request-helpers/failed-request
                                        [::events/fetch-elections-for-jurisdiction
                                         current-partner-slug]])
                        @(rf/subscribe [::request-helpers/failed-request
                                        [::e.voter-lookup/partner-fetch-ballots
                                         {:jurisdiction-id (:partner/id current-partner)
                                          :election-id (:election/id current-election)
                                          :query-params @url-state}]]))]
    (cond
      fetch-error
      (:status-text fetch-error)

      (and current-partner current-election)
      [voter-lookup {:columns partner-columns
                     :get-lookup-route-params partner-lookup-route-params
                     :lookup-route partner-lookup-route
                     :prep-export-fn partner-prep-export!}]

      (some? @(rf/subscribe [::subs/elections-for-partner current-partner-slug]))
      "Election Does Not Exist"

      :else
      "Loading Partner Data")))
